import React, { useEffect, useState } from "react";
import Nav from "../components/Nav";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { toast } from "sonner";
import PostsAdmin from "../components/PostsAdmin";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { lang } = useLang();
  const [token, setToken] = useState(localStorage.getItem("rihaai_admin_token") || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const login = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/admin/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: input }),
      });
      if (!r.ok) throw new Error("bad");
      localStorage.setItem("rihaai_admin_token", input);
      setToken(input);
      toast.success("Signed in");
    } catch {
      toast.error("Invalid token");
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lr, sr] = await Promise.all([
        fetch(`${BACKEND}/api/leads`, { headers: { "X-Admin-Token": token } }),
        fetch(`${BACKEND}/api/admin/stats`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (lr.status === 401 || sr.status === 401) {
        localStorage.removeItem("rihaai_admin_token");
        setToken("");
        toast.error("Session expired");
        return;
      }
      setLeads(await lr.json());
      setStats(await sr.json());
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    await fetch(`${BACKEND}/api/leads/${id}?status=${status}`, {
      method: "PATCH",
      headers: { "X-Admin-Token": token },
    });
    fetchAll();
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <>
        <Nav />
        <main className="pt-40 pb-24 min-h-screen">
          <div className="container-x max-w-md mx-auto">
            <h1 className="font-serif-display text-5xl font-light mb-3" data-testid="admin-title">{pick(T.admin.title, lang)}</h1>
            <p className="text-[#736F66] mb-8">{pick(T.admin.login, lang)}</p>
            <div className="bg-white rounded-3xl p-8 border border-[#D8CDBA] space-y-4">
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                placeholder="Admin token"
                className="w-full px-4 py-3 rounded-xl border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-[#FAF9F6]"
                data-testid="admin-token-input"
              />
              <button onClick={login} className="btn-primary w-full justify-center" data-testid="admin-signin">
                {pick(T.admin.signin, lang)}
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Nav />
      <main className="pt-32 pb-20 min-h-screen">
        <div className="container-x">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="overline mb-2">{pick(T.admin.title, lang)}</div>
              <h1 className="font-serif-display text-5xl font-light" data-testid="admin-heading">
                {tab === "leads" ? pick(T.admin.leads, lang) : "Blog posts"}
              </h1>
            </div>
            <button
              onClick={() => { localStorage.removeItem("rihaai_admin_token"); setToken(""); }}
              className="btn-secondary text-sm"
              data-testid="admin-logout"
            >
              {pick(T.admin.logout, lang)}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-10 border-b border-[#D8CDBA]" data-testid="admin-tabs">
            {[
              { k: "leads", label: pick(T.admin.leads, lang) },
              { k: "posts", label: "Blog Posts" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-5 py-3 text-sm font-medium -mb-px border-b-2 transition ${
                  tab === t.k
                    ? "border-[#2C4C3B] text-[#2C4C3B]"
                    : "border-transparent text-[#736F66] hover:text-[#1A1A1A]"
                }`}
                data-testid={`admin-tab-${t.k}`}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-3 self-center pr-2" data-testid="wp-badge">
              {tab === "posts" && (
                <button
                  onClick={async () => {
                    try {
                      const r = await fetch(`${BACKEND}/api/admin/import-wp`, {
                        method: "POST",
                        headers: { "X-Admin-Token": token },
                      });
                      const d = await r.json();
                      toast.success(`Imported ${d.imported} posts from WordPress (skipped ${d.skipped} existing)`);
                    } catch {
                      toast.error("Import failed");
                    }
                  }}
                  className="text-xs bg-[#2C4C3B] text-white px-3 py-1.5 rounded-full hover:bg-[#1F362A] transition"
                  data-testid="import-wp-btn"
                >
                  Import WP Posts
                </button>
              )}
              <div className="text-xs text-[#736F66]">
                📝 Blog: WordPress (myrihai.wordpress.com)
              </div>
            </div>
          </div>

          {tab === "posts" && <PostsAdmin token={token} />}

          {tab === "leads" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <div className="bg-[#2C4C3B] text-white rounded-2xl p-6" data-testid="stat-total">
                <div className="text-xs uppercase tracking-widest text-[#D8CDBA] mb-2">{pick(T.admin.total, lang)}</div>
                <div className="font-serif-display text-4xl">{stats.total}</div>
              </div>
              {Object.entries(stats.by_status || {}).map(([k, v]) => (
                <div key={k} className="bg-white border border-[#D8CDBA] rounded-2xl p-6" data-testid={`stat-${k}`}>
                  <div className="text-xs uppercase tracking-widest text-[#736F66] mb-2">{k}</div>
                  <div className="font-serif-display text-4xl text-[#2C4C3B]">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Conversions — call / WhatsApp / form taps per page */}
          {tab === "leads" && stats?.events && (
            <div className="mb-10" data-testid="conversions-section">
              <div className="overline mb-4">Conversions</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#A83F32] text-white rounded-2xl p-6" data-testid="conv-calls">
                  <div className="text-xs uppercase tracking-widest text-white/70 mb-2">Call taps</div>
                  <div className="font-serif-display text-4xl">{stats.events.by_event.call_click || 0}</div>
                </div>
                <div className="bg-[#25D366] text-white rounded-2xl p-6" data-testid="conv-whatsapp">
                  <div className="text-xs uppercase tracking-widest text-white/70 mb-2">WhatsApp taps</div>
                  <div className="font-serif-display text-4xl">{stats.events.by_event.whatsapp_click || 0}</div>
                </div>
                <div className="bg-[#2C4C3B] text-white rounded-2xl p-6" data-testid="conv-leads">
                  <div className="text-xs uppercase tracking-widest text-[#D8CDBA] mb-2">Form submissions</div>
                  <div className="font-serif-display text-4xl">{stats.events.by_event.generate_lead || 0}</div>
                </div>
                <div className="bg-white border border-[#D8CDBA] rounded-2xl p-6" data-testid="conv-total">
                  <div className="text-xs uppercase tracking-widest text-[#736F66] mb-2">All interactions</div>
                  <div className="font-serif-display text-4xl text-[#2C4C3B]">
                    {Object.values(stats.events.by_event).reduce((a, b) => a + b, 0)}
                  </div>
                </div>
              </div>
              {/* Search-term analytics — EN + HI split */}
              {stats.events.top_searches?.length > 0 && (
                <div className="mb-6 bg-white border border-[#D8CDBA] rounded-3xl p-6" data-testid="search-analytics">
                  <div className="overline mb-4">Top searches on the Knowledge Hub</div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-[#736F66] mb-3">English</div>
                      <div className="flex flex-wrap gap-2">
                        {stats.events.top_searches.filter(s => s.lang !== "hi").map((s, i) => (
                          <span key={`en-${s.term}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-[#D8CDBA] px-4 py-1.5 text-sm" data-testid={`search-term-en-${i}`}>
                            <span className="font-medium text-[#1A1A1A]">{s.term}</span>
                            <span className="text-xs text-[#736F66]">{s.count}×</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-[#736F66] mb-3">हिंदी (Hindi)</div>
                      <div className="flex flex-wrap gap-2">
                        {stats.events.top_searches.filter(s => s.lang === "hi").length > 0
                          ? stats.events.top_searches.filter(s => s.lang === "hi").map((s, i) => (
                              <span key={`hi-${s.term}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-[#C46B2F]/40 bg-[#FAF3EC] px-4 py-1.5 text-sm" data-testid={`search-term-hi-${i}`}>
                                <span className="font-medium text-[#1A1A1A]">{s.term}</span>
                                <span className="text-xs text-[#736F66]">{s.count}×</span>
                              </span>
                            ))
                          : <span className="text-sm text-[#736F66]">No Hindi searches yet</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {stats.events.by_page.length > 0 && (
                <div className="bg-white border border-[#D8CDBA] rounded-3xl overflow-hidden">
                  <table className="w-full text-sm" data-testid="conv-pages-table">
                    <thead className="bg-[#EAE6DF] text-[#4A4640]">
                      <tr>
                        <th className="text-left p-4">Page</th>
                        <th className="text-left p-4">Call taps</th>
                        <th className="text-left p-4">WhatsApp taps</th>
                        <th className="text-left p-4">Form leads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.events.by_page.map((r) => (
                        <tr
                          key={r.page}
                          className="border-t border-[#D8CDBA]"
                          data-testid={`conv-page-${r.page === "/" ? "home" : r.page.replace(/\W/g, "")}`}
                        >
                          <td className="p-4 font-medium">{r.page}</td>
                          <td className="p-4">{r.calls}</td>
                          <td className="p-4">{r.whatsapp}</td>
                          <td className="p-4">{r.leads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "leads" && (
          <div className="bg-white border border-[#D8CDBA] rounded-3xl overflow-hidden">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#EAE6DF] text-[#4A4640]">
                <tr>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Phone</th>
                  <th className="text-left p-4">Case</th>
                  <th className="text-left p-4">Urgency</th>
                  <th className="text-left p-4">City</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-[#736F66]">Loading…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-[#736F66]">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#D8CDBA]" data-testid={`lead-row-${l.id}`}>
                    <td className="p-4 font-medium">{l.name}</td>
                    <td className="p-4">{l.phone}</td>
                    <td className="p-4">{l.case_type}</td>
                    <td className="p-4">{l.urgency}</td>
                    <td className="p-4">{l.city || "—"}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 text-xs rounded-full ${
                        l.status === "new" ? "bg-[#A83F32]/10 text-[#A83F32]" :
                        l.status === "contacted" ? "bg-[#2C4C3B]/10 text-[#2C4C3B]" :
                        "bg-[#EAE6DF] text-[#736F66]"
                      }`}>{l.status}</span>
                    </td>
                    <td className="p-4">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className="text-xs px-2 py-1 border border-[#D8CDBA] rounded"
                        data-testid={`lead-status-${l.id}`}
                      >
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="closed">closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </main>
    </>
  );
}
